/**
 * Site Contact Hook - CMS Integrated
 *
 * Fetches contact information from Payload CMS Site Settings
 * with static fallback for resilience.
 *
 * Features:
 * - TanStack Query for data fetching with caching
 * - Static fallback when CMS unavailable
 * - `source` flag to indicate data origin
 * - Normalized contact data with formatted hrefs
 *
 * @see Phase 1: Contact Data Fix (CMS Integration)
 */

import { useQuery } from "@tanstack/react-query";
import { CONTACT_DETAILS, FOOTER_CONTENT } from "@/data/footer-content";
import { siteSettingsQueryOptions } from "@/lib/query-options";

/**
 * Normalized contact data structure
 */
export type SiteContact = {
	phone: string;
	phoneHref: string;
	email: string;
	emailHref: string;
	whatsapp?: string;
	whatsappHref?: string;
	location: string;
	businessHours: {
		weekdays: string;
		weekend: string;
	};
	responseTime: string;
	social: {
		instagram: {
			url: string;
			handle: string;
		};
	};
};

/**
 * Hook return type with data source indicator
 */
export type UseSiteContactReturn = {
	data: SiteContact;
	isLoading: boolean;
	isError: boolean;
	/** Indicates whether data came from CMS or static fallback */
	source: "cms" | "static";
};

/**
 * CMS contact settings shape (from Payload SiteSettings global)
 */
type CMSContactSettings = {
	phone?: string;
	email?: string;
	location?: string;
	businessHours?: {
		weekdays?: string;
		weekend?: string;
	};
};

/**
 * Static fallback contact data
 * Used when CMS is unavailable or returns incomplete data
 */
const STATIC_FALLBACK: SiteContact = {
	phone: FOOTER_CONTENT.contact.phone,
	phoneHref: `tel:${FOOTER_CONTENT.contact.phone.replace(/\s/g, "")}`,
	email: FOOTER_CONTENT.contact.email,
	emailHref: `mailto:${FOOTER_CONTENT.contact.email}`,
	whatsapp: FOOTER_CONTENT.contact.whatsapp,
	whatsappHref: FOOTER_CONTENT.contact.whatsapp
		? `https://wa.me/${FOOTER_CONTENT.contact.whatsapp.replace(/\+/g, "")}`
		: undefined,
	location: CONTACT_DETAILS.location,
	businessHours: CONTACT_DETAILS.businessHours,
	responseTime: CONTACT_DETAILS.responseTime,
	social: {
		instagram: {
			url:
				FOOTER_CONTENT.credentials.socialLinks.find(
					(link) => link.platform === "Instagram",
				)?.url ?? "https://instagram.com/bymaryam.bh",
			handle:
				FOOTER_CONTENT.credentials.socialLinks.find(
					(link) => link.platform === "Instagram",
				)?.handle ?? "@bymaryam.bh",
		},
	},
};

/**
 * Normalize CMS contact data with static fallbacks
 */
function normalizeCMSContact(cmsData: CMSContactSettings | null): SiteContact {
	const phone = cmsData?.phone ?? STATIC_FALLBACK.phone;
	const email = cmsData?.email ?? STATIC_FALLBACK.email;

	return {
		phone,
		phoneHref: `tel:${phone.replace(/\s/g, "")}`,
		email,
		emailHref: `mailto:${email}`,
		whatsapp: STATIC_FALLBACK.whatsapp,
		whatsappHref: STATIC_FALLBACK.whatsappHref,
		location: cmsData?.location ?? STATIC_FALLBACK.location,
		businessHours: {
			weekdays:
				cmsData?.businessHours?.weekdays ??
				STATIC_FALLBACK.businessHours.weekdays,
			weekend:
				cmsData?.businessHours?.weekend ??
				STATIC_FALLBACK.businessHours.weekend,
		},
		responseTime: STATIC_FALLBACK.responseTime,
		social: STATIC_FALLBACK.social,
	};
}

/**
 * Hook to fetch site contact information from CMS with static fallback
 *
 * @example
 * ```tsx
 * const { data, isLoading, source } = useSiteContact();
 *
 * return (
 *   <a href={data.emailHref}>{data.email}</a>
 *   {source === 'static' && <span>Using cached data</span>}
 * );
 * ```
 */
export function useSiteContact(): UseSiteContactReturn {
	const { data, isLoading, isError } = useQuery({
		...siteSettingsQueryOptions("contact"),
		// Limit retries to avoid blocking fallback
		retry: 1,
		// Cache for 1 hour since contact info rarely changes
		staleTime: 60 * 60 * 1000,
		// Keep previous data while refetching
		placeholderData: (previousData) => previousData,
	});

	// If error or no data, use static fallback
	if (isError || !data?.value) {
		return {
			data: STATIC_FALLBACK,
			isLoading: false,
			isError,
			source: "static",
		};
	}

	// Normalize CMS data with fallbacks for missing fields
	const normalizedData = normalizeCMSContact(data.value as CMSContactSettings);

	return {
		data: normalizedData,
		isLoading,
		isError: false,
		source: "cms",
	};
}

export default useSiteContact;
